import React, { ChangeEvent, useCallback, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useFormik } from 'formik';

import type { InitialFormValues } from '../../../types';

import { ResponseStatus, URL } from 'app/api/constants';
import { FormControl } from 'app/design-system/components/FormControl';
import { MIN_LEN_PHONE_NUMBER } from 'app/design-system/components/PhoneInput/constants';
import { getDialCode, removeNotNumberCharacter } from 'app/design-system/components/PhoneInput/phone-input-helpers';
import { useTranslation } from 'app/lang';
import usePhoneData from 'app/lib/region/usePhoneData';
import PhoneInput from 'app/widgets/InputsWithAnalytics/PhoneInput';

import { sendRequest } from '../../../actions';
import { DATA_TYPE, INITIAL_FORM_VALUES, USER_DATA_INITIAL_STATE } from '../../../constants';
import { StepContext } from '../../../contexts/stepContext';
// import Tabs from 'app/design-system/components/Tabs';
// import Input from 'app/design-system/components/Input';
import { UserContext } from '../../../contexts/userContext';
import { getValidate } from './initial-form-validations';

import dictionary from './InitialForm.dictionary.json';

import styles from '../../../RestorePasswordDialog.module.scss';

/*
    Закоментированный код возможно в будущем пригодится.
    Когда будет реализована регистрация, станет ясно какие данные мы будем использовать
    для восстановления пользовательского пароля. Телефон, email или все вместе.
*/

const InitialForm = (): JSX.Element => {
  const { t } = useTranslation();
  const { userData, setUserData } = useContext(UserContext);
  const { activeStep, forwardStep } = useContext(StepContext);
  const phoneData = usePhoneData();

  const [error, setError] = useState(false);

  const { requisite } = userData;
  const validate = getValidate(t);
  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    setFieldError,
    setSubmitting,
    setTouched,
    isSubmitting,
  } = useFormik({
    initialValues: INITIAL_FORM_VALUES,
    validate,
    onSubmit: (values: InitialFormValues) => {
      const { phoneNumber } = values;
      const phone = removeNotNumberCharacter(phoneNumber);

      const type = DATA_TYPE.phoneNumber;

      // const emailData: RequestBody = { type, email };
      // const phoneData: RequestBody = { type, phoneNumber: phone };
      // const sendData = type === DataType.email ? emailData : phoneData;

      setError(false);
      setUserData((prevState) => ({ ...prevState, disabled: true }));

      sendRequest(URL.PASSWORD_RESET_SEND_CODE, { type, phoneNumber: phone })
        .then((response) => {
          setUserData((prevState) => ({ ...prevState, ...response.data }));

          forwardStep(activeStep);
        })
        .catch(({ response: { data } }) => {
          const notReady = data.type.endsWith('not-ready-to-resend');
          const limited = data.type.endsWith('request-limited');

          if (limited) {
            const { resendDate, serverTime } = data;
            const { attemptsLeft, attemptsTotal } = USER_DATA_INITIAL_STATE;
            const phone = requisite === '' ? values.phoneNumber : requisite;
            const state = {
              requisite: phone, type, resendDate,
              serverTime, attemptsLeft, attemptsTotal,
            };
            setUserData((prevState) => ({ ...prevState, ...state }));

            forwardStep(activeStep);
          }

          else if (notReady) {
            const { resendDate, serverTime, attemptsLeft, attemptsTotal } = data;
            const state = { requisite, type, resendDate, serverTime, attemptsLeft, attemptsTotal };
            setUserData((prevState) => ({ ...prevState, ...state }));

            forwardStep(activeStep);
          }

          else if (data.status === ResponseStatus.NOT_FOUND) {
            const state = { requisite, type, attemptsLeft: 3, attemptsTotal: 3 };

            setUserData((prevState) => ({ ...prevState, ...state }));

            forwardStep(activeStep);
          }

          else {
            setSubmitting(false);
            setError(true);
            setFieldError('phoneNumber', t(dictionary.submitError));
          }
        })
        .finally(() => setUserData((prevState) => ({ ...prevState, disabled: false })));
    },
  });

  useEffect(() => {
    // setFieldValue('email', userData.email || '', false);

    const initialPhone = sessionStorage.getItem('phone') ?? phoneData.dialCode;

    setFieldValue('phoneNumber', initialPhone, false);

    setUserData((prevState) => ({ ...prevState, requisite: initialPhone }));
  }, []);

  // const initialTab = userData.type === DataType.email ? DataName.EMAIL : DataName.PHONE;
  // const [activeTab, setActiveTab] = useState<DataName>(initialTab);


  const fieldInfo = t(dictionary.phoneFieldInfo);

  const errorInfo = error && t(dictionary.phoneErrorInfo);

  // const handleChangeTab = useCallback((tab: DataName) => {
  //   setActiveTab(tab);
  // }, []);

  const handleOnChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = removeNotNumberCharacter(event.target.value);
    const currentNumber = removeNotNumberCharacter(values.phoneNumber);
    const newNumber = value.slice(currentNumber.length);

    // Todo https://jira.tennisi.it/browse/WEB-1654
    const isCopyPasteFullNumber = value.length - currentNumber.length >= MIN_LEN_PHONE_NUMBER;

    if (isCopyPasteFullNumber) {
      const currentNumberDialCode = getDialCode(currentNumber);
      const newNumberDialCode = getDialCode(newNumber);

      if (currentNumberDialCode === newNumberDialCode) {
        setUserData((prevState) => ({ ...prevState, requisite: newNumber }));
        setFieldValue('phoneNumber', newNumber);
        sessionStorage.setItem('phone', newNumber);

        return;
      }
    }

    setUserData((prevState) => ({ ...prevState, requisite: value }));
    setFieldValue('phoneNumber', value);
    sessionStorage.setItem('phone', value);
  }, [values.phoneNumber]);

  const handleFieldFocus = useCallback(() => {
    setTouched({});
    setError(false);

    // setFieldValue('name', event.target.name);
  }, []);

  // const hasEmailError = !!(errors.email) && touched.email;
  const hasPhoneNumberError = !!(errors.phoneNumber) && touched.phoneNumber;

  const fieldInfoClassNames = classNames({
    [styles.fieldInfo]: !errors.email || !errors.phoneNumber,
    [styles.error]: error,
  });

  if (isSubmitting && errors[values.name] && !error) {
    setError(true);
  }

  return (
    <form id={activeStep.toString()}
          noValidate
          onSubmit={handleSubmit}>
      {/*<Tabs<DataName>*/}
      {/*  data-test="Modal-restorePassword-tabs"*/}
      {/*  type="button"*/}
      {/*  activeTabId={activeTab}*/}
      {/*  tabs={TABS_OPTIONS}*/}
      {/*  className={styles.tabs}*/}
      {/*  onChange={handleChangeTab} />*/}
      <FormControl label={t(dictionary.phoneNumberLabel)}
                   className={styles.restorePasswordForm}>
        <PhoneInput name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleOnChange}
                    placeholder={phoneData.placeholder}
                    error={hasPhoneNumberError}
                    autoFocus
                    autoComplete="on"
                    disabled={isSubmitting}
                    onFocus={handleFieldFocus}
                    data-ga-event="passwordResetNumberInput"
                    data-test="Modal-restorePassword-phoneInput" />
        {/*{activeTab === DataName.EMAIL && (*/}
        {/*  <Input name="email"*/}
        {/*         type="email"*/}
        {/*         value={values.email}*/}
        {/*         placeholder="example@mail.com"*/}
        {/*         error={hasEmailError}*/}
        {/*         autoFocus*/}
        {/*         onFocus={handleFieldFocus}*/}
        {/*         onChange={handleChange}*/}
        {/*         disabled={isSubmitting}*/}
        {/*         className={styles.emailInput}*/}
        {/*         data-test="Modal-restorePassword-emailInput" />*/}
        {/*)}*/}
        <span className={fieldInfoClassNames}
              data-test="modal-restorePasswordText">
          {error ? errorInfo : fieldInfo}
        </span>
      </FormControl>
    </form>
  );
};

export default InitialForm;
